import { useCallback, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DecisionSubjectItem } from 'DecisionPackage/DecisionSubjectModule/components/DecisionSubjectItem';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getDecisionTargetEntitySelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import {
  getJudgmentSubjectDataBlockId,
  getJudgmentSubjectDataItemBlockId,
  getOrganizationBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormJudgment } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgment';
import { IKnmErknmFormJudgmentSubjectData } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormJudgmentSubjectData';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Button } from 'UIPackage/MainModule/components/Button';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { AddJudgmentModal } from './AddJudgmentModal';
import { DecisionJudgmentItemSubjectDataItem } from './DecisionJudgmentItemSubjectDataItem';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IDecisionJudgmentItemSubjectDataProps {
  formRef: IDecisionFormRef | undefined;
  judgmentItemId: string;
  judgmentItemName: string;
  judgmentItemSubjectDataId: string;
  judgmentItemValue: IKnmErknmFormJudgment;
  watchOnly: boolean;
}

interface IDecisionJudgmentItemSubjectDataStoreProps {
  targetEntitySelectOptions?: IDictionarySelectOption<ITargetEntity>[];
}

const DecisionJudgmentItemSubjectDataWithoutStore = ({
  formRef,
  judgmentItemId,
  judgmentItemName,
  judgmentItemSubjectDataId,
  judgmentItemValue,
  watchOnly,
  targetEntitySelectOptions,
}: IDecisionJudgmentItemSubjectDataProps & IDecisionJudgmentItemSubjectDataStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const openModal = () => {
    const organization = getOrganization();

    if (organization) {
      // @ts-ignore
      const judgmentOrganizationList: IKnmErknmFormJudgmentSubjectData[] | undefined = formRef?.current.getFieldState(
        `${judgmentItemName}.judgmentSubjectData` as keyof IDecisionForm,
      )?.value;

      if (!judgmentOrganizationList?.map(x => x.organizationGuid).includes(organization.guid) && push) {
        const newSubjectData: IKnmErknmFormJudgmentSubjectData = {
          organizationGuid: organization.guid,
          isJudgmentAgainstOrganizations: undefined,
          isJudgmentAgainstOfficialPerson: undefined,
          judgmentAgainstOrganizations: undefined,
          judgmentAgainstOfficialPerson: undefined,
          cancellationActsAndDecisions: undefined,
          listId: uuidv4(),
          isNew: true,
        };

        push(`${judgmentItemName}.judgmentSubjectData`, newSubjectData);
      }
    } else {
      setIsModalVisible(true);
    }
  };

  const saveOrganizationModal = () => {
    setIsModalVisible(false);
  };

  const cancelModal = useCallback(() => {
    formRef?.current.change(
      `${judgmentItemName}.judgmentSubjectData` as keyof IDecisionForm,
      // @ts-ignore
      judgmentItemValue.judgmentSubjectData,
    );
    setIsModalVisible(false);
  }, [formRef, judgmentItemValue, judgmentItemName]);
  const makeRemoveJudgmentSubjectDataHandler = (index: number) => () => {
    !watchOnly && remove && remove(`${judgmentItemName}.judgmentSubjectData`, index);
  };

  const getOrganization = () => {
    return formRef?.current.getFieldState('organization')?.value;
  };

  return (
    <>
      <AddJudgmentModal
        formRef={formRef}
        visible={isModalVisible}
        modalType={'organization'}
        judgmentItemName={judgmentItemName}
        onSave={saveOrganizationModal}
        onCancel={cancelModal}
        organization={getOrganization()}
      />
      <Section
        title="Сведения о контролируемом лице"
        type="list"
        id={judgmentItemSubjectDataId}
        titleId={getJudgmentSubjectDataBlockId(judgmentItemId)}
        customButton={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={watchOnly}
            onClick={openModal}
          />
        }
      >
        {!judgmentItemValue.judgmentSubjectData?.length && (
          <p className="shared-input-error">Поле не может быть пустым</p>
        )}
        <FieldArray name={`${judgmentItemName}.judgmentSubjectData`}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormJudgmentSubjectData, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((judgmentItemSubjectDataItemName, index) => {
              const subjectDataValue: IKnmErknmFormJudgmentSubjectData = fields.value?.[index];
              const judgmentItemSubjectDataItemId = getJudgmentSubjectDataItemBlockId(
                judgmentItemId,
                subjectDataValue.listId,
              );
              const organization = formRef?.current.getFieldState('organization')?.value;

              const hasError = checkHasErrorInCollapseBlock(formRef, judgmentItemSubjectDataItemName);

              if (organization) {
                return (
                  <KnmCollapse
                    theme="fields"
                    title="Контролирумое лицо"
                    id={judgmentItemSubjectDataItemId}
                    isNew={judgmentItemValue.isNew}
                    isNewTitle={'Новое'}
                    opened={true}
                    onClose={!watchOnly ? makeRemoveJudgmentSubjectDataHandler(index) : undefined}
                    key={judgmentItemSubjectDataItemName}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [judgmentItemSubjectDataItemName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[judgmentItemSubjectDataItemName]}
                  >
                    <>
                      <DecisionSubjectItem
                        ervkId={organization?.ervkId}
                        checkSubjectResult={organization?.checkSubjectResult}
                        formRef={formRef}
                        getBlockId={getOrganizationBlockId}
                        inn={organization?.inn}
                        isFiz={false}
                        isIt={organization.isPresentInItRegistry}
                        isSonko={organization?.isSonko}
                        itAccreditationResponse={organization.itAccreditationResponse}
                        kpp={organization?.kpp}
                        mspCode={organization?.mspCode}
                        mspRegistryDate={organization?.mspRegistryDate}
                        name="organization"
                        ogrn={organization?.ogrn}
                        organizationType={organization.organizationType}
                        rafpNza={organization?.rafpNza}
                        smevRequestStatus={organization?.smevRequestStatus}
                        snils={organization?.snils}
                        subjectTypeOptions={targetEntitySelectOptions}
                        isWatchOnly={watchOnly}
                        organizationAutocomplete={organization.isAutocomplete}
                        smevItAccreditationRequestStatus={organization.smevItAccreditationRequestStatus}
                      />
                      {subjectDataValue.organizationGuid && (
                        <DecisionJudgmentItemSubjectDataItem
                          judgmentItemName={judgmentItemName}
                          judgmentItemSubjectDataItemName={judgmentItemSubjectDataItemName}
                          judgmentItemValue={judgmentItemValue}
                          watchOnly={watchOnly}
                          cancellationActsAndDecisions={subjectDataValue.cancellationActsAndDecisions}
                          isJudgmentAgainstOfficialPerson={subjectDataValue.isJudgmentAgainstOfficialPerson}
                          isJudgmentAgainstOrganizations={subjectDataValue.isJudgmentAgainstOrganizations}
                          judgmentAgainstOrganizations={subjectDataValue.judgmentAgainstOrganizations}
                          judgmentItemSubjectDataItemId={judgmentItemSubjectDataItemId}
                          formRef={formRef}
                        />
                      )}
                    </>
                  </KnmCollapse>
                );
              }

              return <></>;
            });
          }}
        </FieldArray>
      </Section>
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionJudgmentItemSubjectDataStoreProps => ({
  targetEntitySelectOptions: getDecisionTargetEntitySelectOptions(state),
});

export const DecisionJudgmentItemSubjectData = connect(mapStateToProps)(DecisionJudgmentItemSubjectDataWithoutStore);
